import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES, type Environment } from '../../api';
import { api, useHubLogin } from '../../session';
import { authClientFor, loadTargets, targetSession, useTargets } from '../../identity/session';
import type { Customer, Tenant } from '../../identity/api';
import { stats, type StatsConfig } from '../../api/stats';

/**
 * Where statistics meet the registry (best effort). The collector knows PMS customer ids and free-text WorkServer
 * names; AUTH maps them with aliases (`pms:<customerId>` on a customer, `workserver:<name>` on a tenant) and a registry
 * environment names its tenant (`authTenantId`). Everything is read from the hub's own AUTH (the one the collector
 * trusts, `/stats-config` → `authTarget`) through the identity proxy, so it needs `ophub:auth:read` and a session there;
 * without them the pages simply show no links.
 */

export interface StatsLinks {
  /** Why nothing could be linked (no AUTH access, not signed in, failure). */
  reason: string | null;
  target: string | null;
  environments: Environment[];
  customerByPms: Map<number, Customer>;
  tenantByWorkserver: Map<string, Tenant>;
  tenantById: Map<string, Tenant>;
}

const EMPTY: StatsLinks = {
  reason: null,
  target: null,
  environments: [],
  customerByPms: new Map(),
  tenantByWorkserver: new Map(),
  tenantById: new Map(),
};

const key = (ws: string) => ws.trim().toLowerCase();

async function all<T>(list: (page: number) => Promise<{ items: T[]; total: number }>): Promise<T[]> {
  const first = await list(1);
  const pages = Math.min(20, Math.ceil(first.total / 100));
  const rest = await Promise.all(Array.from({ length: Math.max(0, pages - 1) }, (_, i) => list(i + 2)));
  return [first, ...rest].flatMap((p) => p.items);
}

let config: Promise<StatsConfig> | null = null;
/** `/stats-config`, once per page load. */
export const statsConfig = () =>
  (config ??= stats.config().catch(() => ({ configured: false, collectorUrl: null, authTarget: null })));

/** Every AUTH customer and tenant of `target` (aliases included), for matching. */
export async function loadAuthDirectory(
  target: string
): Promise<{ customers: Customer[]; tenants: Tenant[] }> {
  const c = authClientFor(target);
  const [customers, tenants] = await Promise.all([
    all((page) => c.customers.list({ page, pageSize: 100 })),
    all((page) => c.tenants.list({ page, pageSize: 100 })),
  ]);
  return { customers, tenants };
}

async function load(canAuth: boolean): Promise<StatsLinks> {
  const [cfg, environments] = await Promise.all([
    statsConfig(),
    api.environments().catch(() => [] as Environment[]),
  ]);
  const base = { ...EMPTY, target: cfg.authTarget, environments };
  if (!cfg.authTarget) return { ...base, reason: 'The hub’s AUTH is not one of its AUTH environments.' };
  if (!canAuth) return { ...base, reason: 'Linking to environments needs ophub:auth:read.' };
  await loadTargets();
  if (!targetSession(cfg.authTarget).isSignedIn())
    return { ...base, reason: `Not signed in to AUTH ${cfg.authTarget}.` };
  const { customers, tenants } = await loadAuthDirectory(cfg.authTarget);
  const links: StatsLinks = {
    ...base,
    customerByPms: new Map(),
    tenantByWorkserver: new Map(),
    tenantById: new Map(),
  };
  for (const c of customers)
    for (const a of c.aliases ?? [])
      if (a.system === 'pms' && /^\d+$/.test(a.value)) links.customerByPms.set(Number(a.value), c);
  const customerById = new Map(customers.map((c) => [c.id, c]));
  for (const t of tenants) {
    links.tenantById.set(t.id.toLowerCase(), t);
    // A tenant's own pms alias (one installation of a multi-PMS-customer organization) wins, as in AUTH's resolution.
    const owner = customerById.get(t.customerId);
    if (owner)
      for (const a of t.aliases ?? [])
        if (a.system === 'pms' && /^\d+$/.test(a.value)) links.customerByPms.set(Number(a.value), owner);
    for (const a of t.aliases ?? [])
      if (a.system === 'workserver') links.tenantByWorkserver.set(key(a.value), t);
  }
  return links;
}

const TTL = 120_000;
let cache: { at: number; key: string; data: Promise<StatsLinks> } | null = null;

/** The link directory, cached for 2 minutes (AUTH is not ours to hammer). */
export function useStatsLinks(): StatsLinks & { loading: boolean } {
  const hub = useHubLogin();
  const canAuth = hasScope(hub?.scopes, SCOPES.authRead);
  const { signedIn, targets } = useTargets();
  const signature = `${canAuth}|${targets.map((t) => `${t.id}:${signedIn(t.id)}`).join(',')}`;
  const [state, setState] = useState<StatsLinks & { loading: boolean }>({ ...EMPTY, loading: true });
  useEffect(() => {
    let alive = true;
    if (!cache || cache.key !== signature || Date.now() - cache.at > TTL)
      cache = { at: Date.now(), key: signature, data: load(canAuth) };
    cache.data.then(
      (d) => alive && setState({ ...d, loading: false }),
      (e) => alive && setState({ ...EMPTY, reason: (e as Error).message, loading: false })
    );
    return () => {
      alive = false;
    };
  }, [signature, canAuth]);
  return state;
}

/** Test hook. */
export const resetStatsLinks = () => {
  cache = null;
  config = null;
};

const envsOfTenants = (links: StatsLinks, tenantIds: Set<string>) =>
  links.environments.filter((e) => e.authTenantId && tenantIds.has(e.authTenantId.toLowerCase()));

/** The AUTH customer an organization (PMS customer id) is, by its `pms` alias. */
export const authCustomerOf = (links: StatsLinks, customerId: number) =>
  links.customerByPms.get(customerId) ?? null;

/** Environments of an organization: those whose tenant belongs to the organization's AUTH customer. */
export function envsForOrg(links: StatsLinks, customerId: number): Environment[] {
  const customer = authCustomerOf(links, customerId);
  if (!customer) return [];
  const ids = new Set(
    [...links.tenantById.values()].filter((t) => t.customerId === customer.id).map((t) => t.id.toLowerCase())
  );
  return envsOfTenants(links, ids);
}

/**
 * Environments of one installation: the tenant with alias `workserver:<name>` (exact installation), else the
 * organization's environments (`via: 'organization'`, may be several).
 */
export function envsForInstallation(
  links: StatsLinks,
  customerId: number,
  workserver: string
): { envs: Environment[]; via: 'workserver' | 'organization' | null; tenant: Tenant | null } {
  const tenant = links.tenantByWorkserver.get(key(workserver)) ?? null;
  if (tenant) {
    const envs = envsOfTenants(links, new Set([tenant.id.toLowerCase()]));
    if (envs.length) return { envs, via: 'workserver', tenant };
  }
  const envs = envsForOrg(links, customerId);
  return { envs, via: envs.length ? 'organization' : null, tenant };
}

/** PMS customer ids an environment reports as (its tenant's customer's `pms` aliases). */
export function pmsIdsOfEnv(links: StatsLinks, env: Environment): number[] {
  const tenant = env.authTenantId ? links.tenantById.get(env.authTenantId.toLowerCase()) : undefined;
  if (!tenant) return [];
  return [...links.customerByPms.entries()].filter(([, c]) => c.id === tenant.customerId).map(([id]) => id);
}

/** Decision 11: where an organization runs, derived from its environments (GKE cluster = GCP, none = on-prem). */
export function derivedLocation(envs: Environment[]): { value: string; from: string } | null {
  if (!envs.length) return null;
  const gcp = envs.find((e) => e.clusterId);
  return gcp
    ? { value: 'GCP', from: `environment ${gcp.name} (cluster ${gcp.clusterName ?? gcp.clusterId})` }
    : { value: 'On-prem', from: `environment ${envs[0].name} (no cluster)` };
}
